import { useMemo } from 'react';
import { ToothType } from '../../../../../graphql/graphql';
import { useHealthPlanContext } from '../../../../../Services/HealthPlanContext';
import { useDENTContext } from '../../../Services/DENTContext';

interface UsePeriodontalPointsDetailProps {
	toothType: ToothType;
	base: { w: number; h: number };
	defaultY: number;
	middlePointY: number;
	distance: number;
	margins: number[];
	depths: number[];
}

interface XPoints {
	fixedLeftX: number;
	x1: number;
	x2: number;
	x3: number;
	fixedRightX: number;
}

// X positions are the same for both margin and depth points
const getXPositions = (xPoints: XPoints, width: number) => [
	0, // Far left
	xPoints.fixedLeftX, // Fixed left
	xPoints.x1, // Left-middle
	xPoints.x2, // Center
	xPoints.x3, // Right-middle
	xPoints.fixedRightX, // Fixed right
	width, // Far right
];

export default function usePeriodontalPointsDetail({
	toothType,
	base,
	defaultY,
	middlePointY,
	distance,
	margins,
	depths,
}: UsePeriodontalPointsDetailProps) {
	const { hideMiddlePeriodontalItems } = useDENTContext();

	const xPoints = useMemo(() => {
		// Detail view: position of fixed corner points depends on tooth type
		const fixedLeftX = toothType === ToothType.Molar ? base.w / 10 : base.w / 4;
		const fixedRightX =
			toothType === ToothType.Molar ? (base.w * 9) / 10 : (base.w * 3) / 4;

		const span = fixedRightX - fixedLeftX;
		const x1 = fixedLeftX + span / 4;
		const x2 = fixedLeftX + span / 2;
		const x3 = fixedLeftX + (span * 3) / 4;

		return { fixedLeftX, x1, x2, x3, fixedRightX };
	}, [toothType, base]);

	// Calculate margin Y positions once (used by both margin and depth points)
	const marginYPositions = useMemo(() => {
		const y1 = defaultY - margins[0] * distance;
		const y3 = defaultY - margins[2] * distance;
		// When hideMiddlePeriodontalItems is true, interpolate middle margin value but keep middlePointY offset
		const middleMargin = hideMiddlePeriodontalItems
			? (margins[0] + margins[2]) / 2
			: margins[1];
		const y2 = middlePointY - middleMargin * distance;

		return { y1, y2, y3 };
	}, [defaultY, middlePointY, distance, margins, hideMiddlePeriodontalItems]);

	const marginPoints = useMemo(() => {
		const xPositions = getXPositions(xPoints, base.w);
		return [
			{ x: xPositions[0], y: defaultY },
			{ x: xPositions[1], y: defaultY },
			{ x: xPositions[2], y: marginYPositions.y1 },
			{ x: xPositions[3], y: marginYPositions.y2 },
			{ x: xPositions[4], y: marginYPositions.y3 },
			{ x: xPositions[5], y: defaultY },
			{ x: xPositions[6], y: defaultY },
		];
	}, [xPoints, base.w, defaultY, marginYPositions]);

	const depthPoints = useMemo(() => {
		// Depth line starts from margin line position and adds depth offset
		const y1 = marginYPositions.y1 + depths[0] * distance;
		const y3 = marginYPositions.y3 + depths[2] * distance;
		// When hideMiddlePeriodontalItems is true, interpolate middle depth Y position between left and right
		const y2 = hideMiddlePeriodontalItems
			? Math.max((y1 + y3) / 2, marginYPositions.y2)
			: marginYPositions.y2 + depths[1] * distance;

		const xPositions = getXPositions(xPoints, base.w);
		return [
			{ x: xPositions[0], y: defaultY },
			{ x: xPositions[1], y: defaultY },
			{ x: xPositions[2], y: y1 },
			{ x: xPositions[3], y: y2 },
			{ x: xPositions[4], y: y3 },
			{ x: xPositions[5], y: defaultY },
			{ x: xPositions[6], y: defaultY },
		];
	}, [
		xPoints,
		base.w,
		defaultY,
		marginYPositions,
		depths,
		distance,
		hideMiddlePeriodontalItems,
	]);

	return { marginPoints, depthPoints };
}
